import React, { useMemo, useState } from 'react'
import { Button, InfoSheetPanel, Row, Paragraph } from '@fs/zion-ui'
import cloneDeep from 'lodash.clonedeep'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@fs/zion-flags'
import ZionForm, { useZionForm } from '@fs/zion-form'
import {
  generateSls,
  getImage,
  getNestedOrFloatingElementById,
  getNestedSubelementsByType,
  getPersonName,
  getSlsTypesByKey,
  saveSlsAndUpdateState,
  useSlsDispatchContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { useOverlayContext } from '../../../../../../shared/providers/overlayContextProvider/OverlayContext'
import PanelHeader from '../../shared/PanelHeader'
import RecordAndImageCheckboxes from './RecordAndImageCheckboxes'
import {
  getPrefixedFormDefaultValues,
  getPersonsFromElement,
  getSelectedAndDisplayPersonsByArk,
} from '../utils/selectPersonsUtils'
import { errorCollector } from '../../../../../../shared/utils/utils'
import ErrorOverlay from '../../../../../../shared/components/errorOverlay/ErrorOverlay'
import { headers } from '../../../../../../shared/services/config'

/**
 * Callback function for the onSave event data.
 *
 * @callback onSaveCallback
 * @param {Object[]} dispatches - An array of dispatches.
 * @param {Object[]} formValues - The form values object from the event panel.
 */

/**
 * Callback function for the View In Context button.
 *
 * @callback viewInContextHandler
 * @param {Object[]} pageNumber - image index + 1
 */

/**
 * Component for showing the attachments of an event
 * @param {Object} selectedPerson - the selected person, who is already attached to this event
 * @param {Object} event - the event to display attachments for
 * @param {Boolean} shouldSaveToSls - whether to save the changes to SLS or just pass the dispatches to onSave
 * @param {Function} onSave - Called with dispatches either after or instead of save
 * @param {String} userId - user id for attribution
 * @param {Function} viewInContextHandler - Called with pageNumber. Should display the corresponding image.
 * @param {String} addedEventId - if this event is not yet created, this is the id it will have
 * @param {String} appName - name of the app that is using this component
 * @returns {Element}
 */
export default function SharedEvent({
  selectedPerson,
  event,
  shouldSaveToSls = true,
  onSave = () => {},
  userId,
  viewInContextHandler,
  addedEventId,
  appName,
}) {
  if (!appName) throw new Error('SharedEvent: appName is required parameter')

  const { closeOverlay, sharedEventOverlay } = useOverlayContext()
  const [t] = useTranslation()
  const zionSlsState = useSlsStateContext()
  const zionSlsDispatch = useSlsDispatchContext()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState()
  const { rip_sharedImageViewer_saveChanges: saveChangesFlag } = useFeatureFlags(['rip_sharedImageViewer_saveChanges'])

  const personName = getPersonName({ person: selectedPerson })
  const eventType = event?.eventType
    ? getSlsTypesByKey(zionSlsState.types, 'events').find((type) => type.eventType === event.eventType)
    : null
  const eventTypeLabel = eventType?.displayLabel
  const currentPersonRecord = getNestedOrFloatingElementById(zionSlsState.images, selectedPerson?.recordId)
  const personsInThisRecord = useMemo(
    () => currentPersonRecord?.subElements?.filter((se) => se.elementType === 'PERSON') || [],
    [currentPersonRecord?.subElements]
  )
  const currentImage = getImage(zionSlsState.images, zionSlsState.currentImageArk)
  const personsInThisImage = getNestedSubelementsByType(currentImage, 'PERSON').filter(
    (p) => p.partitionKey === zionSlsState.currentImageArk
  )
  const personsFromElement = useMemo(
    () =>
      getPersonsFromElement({
        element: event,
        images: zionSlsState.images,
        selectedPersonId: selectedPerson.id,
      }),
    [event, selectedPerson.id, zionSlsState.images]
  )

  const allTheArks = useMemo(
    () =>
      getSelectedAndDisplayPersonsByArk({
        allPersonsSharingElement: personsFromElement,
        personsInThisRecord,
      }),
    [personsFromElement, personsInThisRecord]
  )

  const allTheArksExceptCurrent = useMemo(() => {
    if (allTheArks) {
      const clone = cloneDeep(allTheArks)
      delete clone[zionSlsState.currentImageArk]
      return clone
    }
    return {}
  }, [allTheArks, zionSlsState.currentImageArk])

  const personsSharingElementOnImage = useMemo(
    () => allTheArks[zionSlsState.currentImageArk].selected || [],
    [allTheArks, zionSlsState.currentImageArk]
  )

  const allPersonIdsSharingElement = useMemo(
    () => personsSharingElementOnImage.map((p) => p.id),
    [personsSharingElementOnImage]
  )

  const formDefaultValues = useMemo(
    () =>
      getPrefixedFormDefaultValues({
        personIdsSharingElement: allPersonIdsSharingElement,
        allTheArksExceptCurrent,
        personsInThisRecord,
        personsInThisImage,
      }),
    [allPersonIdsSharingElement, personsInThisRecord, personsInThisImage, allTheArksExceptCurrent]
  )

  const handleSubmit = (values) => {
    const mergedValues = { ...values.selectedInImage, ...values.selectedInRecord, ...values.selectedInOtherArks }
    const mergedDefaultValues = {
      ...formDefaultValues.selectedInImage,
      ...formDefaultValues.selectedInRecord,
      ...formDefaultValues.selectedInOtherArks,
    }
    const dispatches = []

    const personIds = Object.keys(mergedValues)
    personIds.forEach((personId) => {
      if (mergedValues[personId] !== mergedDefaultValues[personId]) {
        const editingPerson = getNestedOrFloatingElementById(zionSlsState.images, personId)
        if (mergedValues[personId]) {
          // Link the person to the event
          dispatches.push(
            {
              type: 'ADD_SUB',
              value: {
                superPartitionKey: editingPerson.partitionKey,
                subPartitionKey: event ? event.partitionKey : selectedPerson.partitionKey,
                superId: personId,
                subId: event?.id || addedEventId,
                elementType: 'PERSON',
              },
            },
            {
              type: 'SET_RECORD_PERSON_IDS',
              value: editingPerson.recordId,
            }
          )
        } else {
          // Remove the person from the event
          dispatches.push({
            type: 'REM_SUB',
            value: {
              superPartitionKey: editingPerson.partitionKey,
              subPartitionKey: event.partitionKey,
              superId: personId,
              subId: event.id,
              elementType: 'PERSON',
            },
          })
        }
      }
    })

    if (shouldSaveToSls) {
      try {
        const sls = generateSls(dispatches)
        setSaving(true)
        saveSlsAndUpdateState({
          sls,
          dispatches,
          saveChangesFlag,
          userId,
          dispatch: zionSlsDispatch,
          appName,
          fsUserAgentChain: headers['FS-User-Agent-Chain'],
          successHandler: () => {
            setSaving(false)
            closeOverlay('sharedEvent')
            onSave(dispatches, values)
          },
          errorHandler: (err) => {
            setSaving(false)
            setError(err)
            errorCollector(err, null, {
              codeId: 80,
              sls,
              status: err?.response?.status,
              responseBody: err?.response?.data,
            })
          },
        })
      } catch (err) {
        setSaving(false)
        setError(err)
        errorCollector(err, null, {
          codeId: 80,
          sls: err?.sls,
          dispatches: err?.dispatches,
        })
      }
    } else {
      onSave(dispatches, values)
      closeOverlay('sharedEvent')
    }
  }

  const methods = useZionForm({
    mode: 'all',
    defaultValues: formDefaultValues,
  })

  // The back arrow on panel 2 was clicked
  const userClosedPanel = () => {
    closeOverlay('sharedEvent')
  }

  return (
    <InfoSheetPanel
      title={t('shared-event.title', 'Review {personName}', { personName })}
      headingColor="red"
      {...{ ...sharedEventOverlay, close: userClosedPanel }}
      footer={
        <Row alignX="end" gutters="md">
          <Button
            data-testid="shared-event-back-button"
            linkName="Shared Event Back Button"
            emphasis="low"
            disabled={saving}
            onClick={() => {
              closeOverlay('sharedEvent')
            }}
          >
            {t('shared-event.cancel.btn', 'Cancel')}
          </Button>
          <Button
            data-testid="shared-event-forward-button"
            linkName="Shared Event Forward Button"
            emphasis="high"
            disabled={saving}
            form="shared-event"
            type="submit"
          >
            {t('shared-event.update.btn', 'Update')}
          </Button>
        </Row>
      }
    >
      <PanelHeader
        heading={t('shared-event.panel-heading', 'Link {eventType} to Other Names', { eventType: eventTypeLabel })}
        caption={t('shared-event.panel-caption', 'Names associated with this event')}
      />
      <Paragraph size="sm">
        {t('shared-event.panel-description', 'Do more names need to be linked to this {eventType}?', {
          eventType: eventTypeLabel,
        })}
      </Paragraph>
      <ZionForm methods={methods} onSubmit={handleSubmit} id="shared-event">
        <RecordAndImageCheckboxes
          personsInThisRecord={personsInThisRecord}
          personsInThisImage={personsInThisImage}
          allTheArksExceptCurrent={allTheArksExceptCurrent}
          selectedPerson={selectedPerson}
          viewInContextHandler={viewInContextHandler}
        />
      </ZionForm>
      <ErrorOverlay
        error={error}
        setError={setError}
        title={t('save-error-overlay.heading', 'Unable to Save Changes')}
        message={t('save-error-overlay.content-generic', 'Your changes were not saved.  Please try again later.')}
      />
    </InfoSheetPanel>
  )
}
